import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Loader2, Check, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

type DocumentTag = Tables<"document_tags">;
type Document = Tables<"documents">;

interface TagManagerProps {
  document: Document | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTagsUpdated?: () => void;
}

const TAG_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#f43f5e", // rose
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#3b82f6", // blue
];

export function TagManager({ document, open, onOpenChange, onTagsUpdated }: TagManagerProps) {
  const [tags, setTags] = useState<DocumentTag[]>([]);
  const [documentTags, setDocumentTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTagName, setNewTagName] = useState("");
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0]);
  const [creating, setCreating] = useState(false);

  const fetchTags = async () => {
    setLoading(true);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Fetch all user tags
    const { data: allTags } = await supabase
      .from("document_tags")
      .select("*")
      .eq("user_id", user.id)
      .order("name");

    if (allTags) setTags(allTags);

    // Fetch document's current tags
    if (document) {
      const { data: docTags } = await supabase
        .from("document_tag_links")
        .select("tag_id")
        .eq("document_id", document.id);

      if (docTags) {
        setDocumentTags(docTags.map((t) => t.tag_id));
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchTags();
    }
  }, [open, document]);

  const createTag = async () => {
    if (!newTagName.trim()) return;

    setCreating(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("document_tags")
      .insert({
        name: newTagName.trim(),
        color: selectedColor,
        user_id: user.id,
      })
      .select()
      .single();

    if (error) {
      toast.error("Erreur lors de la création du tag");
    } else if (data) {
      setTags((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTagName("");
      toast.success("Tag créé");
    }

    setCreating(false);
  };

  const deleteTag = async (tagId: string) => {
    const { error } = await supabase
      .from("document_tags")
      .delete()
      .eq("id", tagId);

    if (error) {
      toast.error("Erreur lors de la suppression");
    } else {
      setTags((prev) => prev.filter((t) => t.id !== tagId));
      setDocumentTags((prev) => prev.filter((id) => id !== tagId));
      toast.success("Tag supprimé");
    }
  };

  const toggleDocumentTag = async (tagId: string) => {
    if (!document) return;

    const isSelected = documentTags.includes(tagId);

    if (isSelected) {
      // Remove tag
      const { error } = await supabase
        .from("document_tag_links")
        .delete()
        .eq("document_id", document.id)
        .eq("tag_id", tagId);

      if (!error) {
        setDocumentTags((prev) => prev.filter((id) => id !== tagId));
        onTagsUpdated?.();
      }
    } else {
      // Add tag
      const { error } = await supabase
        .from("document_tag_links")
        .insert({
          document_id: document.id,
          tag_id: tagId,
        });

      if (!error) {
        setDocumentTags((prev) => [...prev, tagId]);
        onTagsUpdated?.();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Gérer les tags
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Create new tag */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Créer un nouveau tag</p>
              <div className="flex gap-2">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Nom du tag..."
                  onKeyDown={(e) => e.key === "Enter" && createTag()}
                />
                <Button onClick={createTag} disabled={creating || !newTagName.trim()}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={cn(
                      "w-6 h-6 rounded-full transition-all",
                      selectedColor === color && "ring-2 ring-offset-2 ring-primary"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Tags list */}
            <div className="space-y-3">
              <p className="text-sm font-medium">
                {document ? "Sélectionnez les tags" : "Vos tags"}
              </p>
              {tags.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Aucun tag créé
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const isSelected = documentTags.includes(tag.id);
                    return (
                      <div key={tag.id} className="flex items-center gap-1">
                        <Badge
                          variant="outline"
                          className={cn(
                            "cursor-pointer transition-all",
                            isSelected && "ring-2 ring-primary"
                          )}
                          style={{
                            backgroundColor: isSelected ? tag.color || "#6366f1" : "transparent",
                            borderColor: tag.color || "#6366f1",
                            color: isSelected ? "white" : tag.color || "#6366f1",
                          }}
                          onClick={() => document && toggleDocumentTag(tag.id)}
                        >
                          {isSelected && <Check className="w-3 h-3 mr-1" />}
                          {tag.name}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5 opacity-50 hover:opacity-100"
                          onClick={() => deleteTag(tag.id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
